import React, { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import type { DocumentReferencesView } from 'api';
import { DropProps } from '../CurrentRequestedDocumentsAndQuestions/types';

// Styled component for drag-over styling
export const DragOverContainer = styled('div')(({ theme }) => ({
  '&.drag-over': {
    outline: `1.5px dashed ${theme.palette.primary.main}`,
  },
}));

// CSS class name for drag over state
const DRAG_OVER_CLASS = 'drag-over';

export const useDragRow = (setDraggedRow: (row: DocumentReferencesView | null) => void) => {
  const handleDragStart = useCallback(
    (event: React.DragEvent<HTMLTableRowElement>, row: DocumentReferencesView) => {
      setDraggedRow(row);
      // eslint-disable-next-line no-param-reassign
      event.dataTransfer.effectAllowed = 'move';
    },
    [setDraggedRow]
  );

  const handleDragEnd = useCallback(() => {
    setDraggedRow(null);
  }, [setDraggedRow]);

  return { handleDragStart, handleDragEnd };
};

export const useDropRow = (props: DropProps) => {
  const { draggedRow, addFilesToFolder, refreshDocuments } = props;

  const handleDragOver = (event: React.DragEvent<HTMLTableRowElement | HTMLTableSectionElement>) => {
    event.preventDefault();
    event.currentTarget.classList.add(DRAG_OVER_CLASS);
  };

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLTableRowElement | HTMLTableSectionElement>) => {
      event.currentTarget.classList.remove(DRAG_OVER_CLASS);
    },
    []
  );

  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLTableRowElement | HTMLTableSectionElement>, folderId?: number) => {
      event.preventDefault();
      if (draggedRow?.document_reference_id) {
        const data = {
          folder_id: folderId,
          document_references_ids: [draggedRow?.document_reference_id],
        };
        handleDragLeave(event);
        await addFilesToFolder(draggedRow.md_documents, data, true);
        refreshDocuments();
      }
    },
    [draggedRow, addFilesToFolder, refreshDocuments, handleDragLeave]
  );

  return { handleDragOver, handleDragLeave, handleDrop };
};
